import React, { useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { loginUser } from "../loginSlice";
import { setBackButtonUrl } from "../uiSlice";
import Swal from "sweetalert2";
import Splash from "../game/Splash";
import { logoFor } from "../game/hoc/logo";
import "../common/saveThem.css";

/**
 * Login = OTP check + the game's title screen. The game is static (no admin theme),
 * so nothing here depends on theme data; "Start the Season" opens /game.
 */
function Login() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { status, user, error } = useSelector((state) => state.auth);

  const [searchParams] = useSearchParams();
  const isAdmin = searchParams.get("admin") === "true";

  useEffect(() => {
    dispatch(loginUser());
  }, [dispatch]);

  useEffect(() => {
    if (status === "succeeded" && user && isAdmin) {
      navigate("/admin");
    }
  }, [status, user, isAdmin, navigate]);

  useEffect(() => {
    if (status === "succeeded" && user?.backButtonRedirect) {
      dispatch(setBackButtonUrl(user.backButtonRedirect));
    }
  }, [status, user, dispatch]);

  useEffect(() => {
    if (status !== "failed") return;
    const msg = error ? String(error) : "Session expired";
    const redirect = user && user.backButtonRedirect ? user.backButtonRedirect : "/";
    Swal.fire("Session Expired!", msg, "error").then(() => {
      window.location.href = redirect;
    });
  }, [status, error, user]);

  // Players who already finished are sent back to the base game page
  const alreadyPlayed = Number(user?.gameover ?? 0) > 0;
  useEffect(() => {
    if (isAdmin || status !== "succeeded" || !user) return;
    if (alreadyPlayed) {
      Swal.fire({
        title: "Already Played",
        text: "You have already played this game.",
        icon: "info",
        confirmButtonText: "OK",
      }).then(() => {
        window.location.href = user.backButtonRedirect || process.env.REACT_APP_BASE_URL || "/";
      });
    }
  }, [status, user, isAdmin, alreadyPlayed]);

  const canPlay = status === "succeeded" && !!user && !alreadyPlayed && !isAdmin;

  if (canPlay) {
    // the click that starts the season is also what lets the browser play the music
    return <Splash logo={logoFor(user?.organizationId)} onStart={() => navigate("/game", { state: { fromLogin: true } })} />;
  }

  return (
    <div className="st-loader">
      <div className="st-loader-spinner" />
    </div>
  );
}

export default Login;
